import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { KbdKeys } from '@client/components/_ui/display/Kbd';
import { Input, Popover } from 'antd';
import { Plus, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Filter, FilterConfig } from '../core/types';

interface AddFilterPopoverProps<TWhere> {
    config: FilterConfig<TWhere>;
    filters: Filter[];
    onAddFilter: (filterId: string) => string | undefined;
}

/**
 * AddFilterPopover Component
 *
 * A popover triggered by the "+ Filter" button that shows a searchable
 * list of available filter fields. Clicking a field adds a new filter.
 *
 * Features:
 * - Searchable field list
 * - Excludes already-active filters (unless field allows multiple)
 * - Excludes pinned fields (they're always visible)
 * - Keyboard shortcut: Cmd+Shift+K / Ctrl+Shift+K to open
 */
export function AddFilterPopover<TWhere>({ config, filters, onAddFilter }: AddFilterPopoverProps<TWhere>) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const inputRef = useRef<any>(null);

    // Get available filters (exclude already active unless they allow multiple, exclude pinned)
    const availableFields = useMemo(() => {
        return config.filters
            .filter((filterConfig) => {
                // Exclude pinned filters - they're always visible
                if (filterConfig.pinned) return false;
                // Check if this filter is already active
                const isActive = filters.some((f) => f.filterId === filterConfig.id);
                // Allow if not active or if filter explicitly allows multiple instances
                return !isActive || filterConfig.multiple === true;
            })
            .map((filterConfig) => ({
                id: filterConfig.id,
                label: filterConfig.label || filterConfig.id,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [config.filters, filters]);

    // Filter by search query
    const filteredFields = useMemo(() => {
        if (!searchQuery) return availableFields;
        const query = searchQuery.toLowerCase();
        return availableFields.filter((field) => field.label.toLowerCase().includes(query));
    }, [availableFields, searchQuery]);

    // Handle field selection
    const handleSelectField = useCallback(
        (fieldId: string) => {
            onAddFilter(fieldId);
            setIsOpen(false);
            setSearchQuery('');
        },
        [onAddFilter],
    );

    // Focus input when popover opens
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    }, [isOpen]);

    // The global command palette owns Cmd/Ctrl+K; Shift keeps this page-local
    // field search reachable without competing capture-phase listeners.
    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                setIsOpen(true);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, []);

    // Handle keyboard navigation in the list
    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
                setSearchQuery('');
            } else if (event.key === 'Enter' && filteredFields.length === 1) {
                // If there's only one result, select it on Enter
                handleSelectField(filteredFields[0].id);
            }
        },
        [filteredFields, handleSelectField],
    );

    const popoverContent = (
        <div className="w-[240px]" onKeyDown={handleKeyDown}>
            {/* Search input */}
            <div className="border-separator border-b px-2 pt-1 pb-2">
                <Input
                    ref={inputRef}
                    placeholder="Search fields..."
                    prefix={<Search size={14} className="text-subtle-foreground" />}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    allowClear
                />
            </div>

            {/* Field list */}
            <div className="max-h-[260px] overflow-y-auto px-1.5 py-1">
                {filteredFields.length === 0 ? (
                    <div className="py-6 text-center text-muted-foreground text-sm">
                        {searchQuery ? 'No matching fields' : 'All filters added'}
                    </div>
                ) : (
                    filteredFields.map((field) => (
                        <button
                            key={field.id}
                            onClick={() => handleSelectField(field.id)}
                            className="w-full rounded px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted"
                        >
                            {field.label}
                        </button>
                    ))
                )}
            </div>

            {/* Keyboard hint — `Mod` resolves to ⌘ on Apple hardware and Ctrl
                elsewhere, matching the modifier the handler above accepts, so a
                Windows or Linux user is never shown a key their platform does
                not use. */}
            <div className="flex items-center justify-end gap-1 border-separator border-t px-2 py-1.5 text-subtle-foreground">
                <KbdKeys keys={['Mod', 'Shift', 'K']} />
            </div>
        </div>
    );

    return (
        <Popover
            content={popoverContent}
            trigger="click"
            open={isOpen}
            onOpenChange={setIsOpen}
            placement="bottomLeft"
            arrow={false}
        >
            <Button type="secondary" icon={<Plus />}>
                Filter
            </Button>
        </Popover>
    );
}
